import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import YAML from "yaml";
import type { Schemas } from "@polaris/api";
import { Badge, Button, Dialog, ErrorBox, Field, Select, type TreeNode } from "@polaris/ui";
import { api, periods, useAction, useApi } from "./lib";
import { t } from "./i18n";

export type Tenant = Schemas["TenantView"];

/** Nomes das estratégias de economia de tokens (RF-ECO-01 a 05). */
export const strategyLabels: Record<string, string> = {
  compress: "Compressão do prompt",
  history: "Corte do histórico",
  tools: "Ferramentas sob demanda",
  concise: "Respostas concisas",
  cache: "Cache",
};

/** Estratégias ligadas numa política de economia de tokens, em texto curto. */
export function policySummary(p?: Schemas["TokenSavingPolicy"]): string[] {
  if (!p?.enabled) return [];
  const out: string[] = [];
  if (p.compress) out.push(t("compressão"));
  if (p.history_max_turns) out.push(t("histórico: {n} trocas", { n: p.history_max_turns }));
  if (p.history_max_percent) out.push(t("histórico: {n}% da janela", { n: p.history_max_percent }));
  if (p.prune_tools) out.push(t("ferramentas sob demanda"));
  if (p.max_output_tokens) out.push(t("saída até {n} tokens", { n: p.max_output_tokens.toLocaleString("pt-BR") }));
  if (p.concise) out.push(t("concisão"));
  return out;
}

export function useTenants() {
  return useApi<Tenant[]>("/api/v1/tenants");
}

const kindLabel: Record<string, string> = { org: "organização", team: "time", project: "projeto", env: "ambiente" };
export const kindName = (k?: string) => t(kindLabel[k ?? ""] ?? k ?? "");

export function buildTree(ts: Tenant[], meta?: (t: Tenant) => React.ReactNode, label?: (t: Tenant) => React.ReactNode): TreeNode[] {
  const by: Record<string, TreeNode & { t: Tenant }> = {};
  ts.forEach((x) => {
    by[x.id] = {
      id: x.id,
      label: label ? label(x) : <span>{x.name || x.id}{x.archived && <span className="muted"> ({t("arquivado")})</span>}</span>,
      children: [],
      t: x,
      meta: meta?.(x),
    };
  });
  const roots: TreeNode[] = [];
  ts.forEach((x) => {
    const n = by[x.id];
    if (x.parent && by[x.parent]) by[x.parent].children!.push(n);
    else roots.push(n);
  });
  return roots;
}

/** Lista os tenants indentados, para selects. */
export function TenantSelect({ value, onChange, kinds, allowEmpty, emptyLabel, id }: {
  value: string; onChange: (v: string) => void; kinds?: string[]; allowEmpty?: boolean; emptyLabel?: string; id?: string;
}) {
  const { data } = useTenants();
  const items = useMemo(() => {
    const out: { id: string; label: string; kind: string }[] = [];
    const walk = (nodes: TreeNode[], depth: number) => nodes.forEach((n: any) => {
      out.push({ id: n.id, label: "  ".repeat(depth) + (n.t.name || n.id), kind: n.t.kind });
      walk(n.children ?? [], depth + 1);
    });
    walk(buildTree((data ?? []).filter((x) => !x.archived)), 0);
    return out;
  }, [data]);
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty && <option value="">{emptyLabel ?? t("Todos os tenants")}</option>}
      {items.map((i) => <option key={i.id} value={i.id} disabled={!!kinds && !kinds.includes(i.kind)}>{i.label} ({kindName(i.kind)})</option>)}
    </Select>
  );
}

export function ManagedBadge({ meta }: { meta?: { managed_by?: string; origin?: { repo?: string; commit?: string } } }) {
  if (meta?.managed_by !== "git") return null;
  return <Badge kind="git" title={`${meta.origin?.repo ?? ""}@${meta.origin?.commit ?? ""}`}>{t("Gerido por Git")} · {meta.origin?.repo}@{(meta.origin?.commit ?? "").slice(0, 7)}</Badge>;
}

export function PeriodSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label={t("Período")}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {periods.map((p) => <option key={p.id} value={p.id}>{t(p.label)}</option>)}
      </Select>
    </Field>
  );
}

// ----- filtros salvos (seção 3: busca, filtros salvos, link direto) -----

export function SavedFilters({ screen }: { screen: string }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { data } = useApi<any[]>("/api/v1/filters", { screen });
  const [name, setName] = useState("");
  const save = useAction((n: string) => api.post("/api/v1/filters", { name: n, screen, query: loc.search, shared: false }), { success: t("Filtro salvo") });
  return (
    <div className="row small" aria-label={t("Filtros salvos")}>
      {(data ?? []).map((f) => (
        <Button key={f.id} small variant="ghost" onClick={() => nav(loc.pathname + f.query)}>★ {f.name}</Button>
      ))}
      <input aria-label={t("Nome do filtro")} placeholder={t("Nome do filtro")} value={name} onChange={(e) => setName(e.target.value)} style={{ width: 150 }} />
      <Button small disabled={!name || !loc.search} onClick={() => { save.mutate(name); setName(""); }}>{t("Salvar filtro")}</Button>
    </div>
  );
}

// ----- editor YAML com schema, autocompletar e validação em tempo real (RF-ADM-POL-01) -----

export function usePolicySchema() {
  return useApi<{ schemas: Record<string, any>; pii_entities: string[]; moderation_categories: string[]; strategies: string[] }>("/api/v1/policies/schema");
}

function keysAt(schema: any, path: string[]): string[] {
  let s = schema;
  for (const p of path) {
    if (!s) return [];
    if (s.type === "array") s = s.items;
    s = s?.properties?.[p] ?? (s?.type === "array" ? s.items : undefined);
  }
  if (s?.type === "array") s = s.items;
  return Object.keys(s?.properties ?? {});
}

/** Descobre o caminho de chaves na linha do cursor pela indentação do YAML. */
function pathAtCursor(text: string, pos: number): string[] {
  const lines = text.slice(0, pos).split("\n");
  const cur = lines[lines.length - 1];
  const indent = cur.match(/^(\s*-?\s*)/)?.[0].length ?? 0;
  const path: string[] = [];
  let need = indent;
  for (let i = lines.length - 2; i >= 0 && need > 0; i--) {
    const m = lines[i].match(/^(\s*)(-\s+)?([\w.-]+):\s*$/);
    if (!m) continue;
    const ind = m[1].length + (m[2]?.length ?? 0);
    if (ind < need) {
      path.unshift(m[3]);
      need = ind;
    }
  }
  return path;
}

export function YamlEditor({ value, onChange, schema, label, readOnly, rows = 16 }: {
  value: unknown; onChange: (v: unknown, ok: boolean) => void; schema?: any; label: string; readOnly?: boolean; rows?: number;
}) {
  const [text, setText] = useState(() => YAML.stringify(value ?? {}));
  const [err, setErr] = useState<string | null>(null);
  const [hint, setHint] = useState<string[]>([]);
  const ref = React.useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    // Reflete mudanças vindas do formulário sem perder o texto em edição quando é o mesmo valor.
    try {
      if (JSON.stringify(YAML.parse(text) ?? {}) !== JSON.stringify(value ?? {})) setText(YAML.stringify(value ?? {}));
    } catch {
      setText(YAML.stringify(value ?? {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);
  const update = (txt: string, pos: number) => {
    setText(txt);
    try {
      const v = YAML.parse(txt) ?? {};
      setErr(null);
      onChange(v, true);
    } catch (e) {
      setErr((e as Error).message.split("\n")[0]);
      onChange(null, false);
    }
    if (schema) setHint(keysAt(schema, pathAtCursor(txt, pos)));
  };
  const insert = (k: string) => {
    const el = ref.current!;
    const pos = el.selectionStart;
    const next = text.slice(0, pos) + k + ": " + text.slice(pos);
    update(next, pos + k.length + 2);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(pos + k.length + 2, pos + k.length + 2); });
  };
  return (
    <div className="field full">
      <label htmlFor="yaml-editor">{label}</label>
      <textarea id="yaml-editor" ref={ref} rows={rows} value={text} readOnly={readOnly} spellCheck={false} aria-invalid={!!err}
        onChange={(e) => update(e.target.value, e.target.selectionStart)} onKeyUp={(e) => schema && setHint(keysAt(schema, pathAtCursor(text, (e.target as HTMLTextAreaElement).selectionStart)))}
        onClick={(e) => schema && setHint(keysAt(schema, pathAtCursor(text, (e.target as HTMLTextAreaElement).selectionStart)))} />
      {err ? <span className="err" role="alert">{t("YAML inválido")}: {err}</span>
        : hint.length > 0 && !readOnly && (
          <div className="row small" aria-label={t("Campos possíveis aqui")}>
            <span className="muted">{t("Campos possíveis aqui")}:</span>
            {hint.map((k) => <Button key={k} small variant="ghost" onClick={() => insert(k)}>{k}</Button>)}
          </div>
        )}
    </div>
  );
}

// ----- solicitações para a fila de aprovações (RF-ADM-APR-01) -----

const requestLabels: Record<string, string> = {
  project: "Novo projeto", alias_access: "Acesso a alias", mcp_tool: "Ferramenta MCP", budget: "Orçamento",
  quota_increase: "Aumento temporário de quota", log_access: "Ver logs com conteúdo", role_elevation: "Elevação temporária de papel",
  mcp_publish: "Publicar MCP", mcp_call: "Chamada MCP", budget_exceeded: "Orçamento estourado", rollout: "Rollout",
  project_join: "Entrar em projeto",
};
export const requestName = (k: string) => t(requestLabels[k] ?? k);

export function RequestDialog({ open, onClose, type, tenant, initial }: { open: boolean; onClose: () => void; type: string; tenant?: string; initial?: Record<string, any> }) {
  const [form, setForm] = useState<Record<string, any>>({});
  const [reason, setReason] = useState("");
  const [ten, setTen] = useState(tenant ?? "");
  useEffect(() => { if (open) { setForm(initial ?? {}); setReason(""); setTen(tenant ?? ""); } }, [open, initial, tenant]);
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const create = useAction(() => {
    let payload: any = { ...form };
    if (type === "alias_access") payload = { aliases: String(form.aliases ?? "").split(",").map((s) => s.trim()).filter(Boolean) };
    if (type === "mcp_tool") payload = { server: form.server, tool: form.tool, tenant: ten, agent: form.agent || "*", operations: String(form.operations ?? "read").split(",").map((s) => s.trim()) };
    if (type === "budget") payload = { budget: { id: form.id || undefined, period: form.period ?? "month", amount: Number(form.amount), currency: form.currency ?? "BRL", alerts_at: [80, 95] } };
    if (type === "quota_increase") payload = { budget_id: form.budget_id, amount: Number(form.amount), hours: Number(form.hours || 168) };
    if (type === "log_access") payload = { hours: Number(form.hours || 1) };
    if (type === "role_elevation") payload = { role: form.role, hours: Number(form.hours || 1) };
    if (type === "project") payload = { ...form, create_envs: ["dev", "hml", "prod"] };
    return api.post("/api/v1/approvals", { type, tenant: ten || undefined, reason, payload });
  }, { success: t("Solicitação enviada para aprovação") });
  const needsTenant = ["alias_access", "mcp_tool", "budget", "quota_increase"].includes(type);
  return (
    <Dialog open={open} onClose={onClose} title={requestName(type)}
      footer={<><Button onClick={onClose}>{t("Cancelar")}</Button><Button variant="primary" loading={create.isPending} disabled={!reason || (needsTenant && !ten)} onClick={() => create.mutate(undefined, { onSuccess: onClose })}>{t("Enviar")}</Button></>}>
      <div className="form">
        {needsTenant && <Field label={t("Tenant")} full><TenantSelect value={ten} onChange={setTen} allowEmpty emptyLabel={t("Escolha")} /></Field>}
        {type === "project" && <>
          <Field label={t("Id do projeto")}><input value={form.id ?? ""} onChange={(e) => set("id", e.target.value)} /></Field>
          <Field label={t("Time")}><TenantSelect value={form.parent ?? ""} onChange={(v) => set("parent", v)} kinds={["team"]} allowEmpty emptyLabel={t("Escolha")} /></Field>
          <Field label={t("Nome")}><input value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label={t("Centro de custo")}><input value={form.cost_center ?? ""} onChange={(e) => set("cost_center", e.target.value)} /></Field>
          <Field label={t("Base legal (LGPD)")}><input value={form.legal_basis ?? ""} onChange={(e) => set("legal_basis", e.target.value)} /></Field>
          <Field label={t("Finalidade (LGPD)")}><input value={form.purpose ?? ""} onChange={(e) => set("purpose", e.target.value)} /></Field>
        </>}
        {type === "alias_access" && <Field label={t("Aliases (separados por vírgula)")} full><input value={form.aliases ?? ""} onChange={(e) => set("aliases", e.target.value)} /></Field>}
        {type === "mcp_tool" && <>
          <Field label={t("Servidor")}><input value={form.server ?? ""} onChange={(e) => set("server", e.target.value)} /></Field>
          <Field label={t("Ferramenta (ou *)")}><input value={form.tool ?? ""} onChange={(e) => set("tool", e.target.value)} /></Field>
          <Field label={t("Agente (sujeito, chave ou *)")}><input value={form.agent ?? ""} onChange={(e) => set("agent", e.target.value)} /></Field>
          <Field label={t("Operações")} hint="read, write"><input value={form.operations ?? "read"} onChange={(e) => set("operations", e.target.value)} /></Field>
        </>}
        {type === "budget" && <>
          <Field label={t("Período")}><Select value={form.period ?? "month"} onChange={(e) => set("period", e.target.value)}>{["day", "month"].map((p) => <option key={p}>{p}</option>)}</Select></Field>
          <Field label={t("Valor")}><input type="number" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label={t("Moeda")}><Select value={form.currency ?? "BRL"} onChange={(e) => set("currency", e.target.value)}><option>BRL</option><option>USD</option></Select></Field>
        </>}
        {type === "quota_increase" && <>
          <Field label={t("Id do orçamento")}><input value={form.budget_id ?? ""} onChange={(e) => set("budget_id", e.target.value)} /></Field>
          <Field label={t("Aumento")}><input type="number" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label={t("Validade (horas)")}><input type="number" value={form.hours ?? 168} onChange={(e) => set("hours", e.target.value)} /></Field>
        </>}
        {(type === "log_access" || type === "role_elevation") && <Field label={t("Duração (horas)")}><input type="number" min={1} max={type === "log_access" ? 24 : 12} value={form.hours ?? 1} onChange={(e) => set("hours", e.target.value)} /></Field>}
        {type === "role_elevation" && <Field label={t("Papel")}><Select value={form.role ?? ""} onChange={(e) => set("role", e.target.value)}><option value="">{t("Escolha")}</option>{["platform-admin", "security-admin", "finops", "auditor"].map((r) => <option key={r}>{r}</option>)}</Select></Field>}
        <Field label={t("Por que você precisa?")} full><textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={{ fontFamily: "inherit" }} /></Field>
      </div>
      <ErrorBox error={create.error} />
    </Dialog>
  );
}
